<template>
  <div class="saves">
    <slot />
    <h4>Saves</h4>
    <SkillRow
      v-for="save in allSaves"
      :key="save.name"
      :dice="save.expr"
      :name="save.name"
      :level="save.level"
      :label="save.label"
    />
    <template v-if="resistances.length">
      <h4>Resistances</h4>
      <div class="damage-types">
        <StatisticIcon v-for="dt in resistances" :key="dt" :name="dt" :icon="dt" :value="''" constant condensed />
      </div>
    </template>
    <template v-if="immunities.length">
      <h4>Immunities</h4>
      <div class="damage-types">
        <StatisticIcon v-for="dt in immunities" :key="dt" :name="dt" :icon="dt" :value="''" constant condensed />
      </div>
    </template>
    <template v-if="vulnerabilities.length">
      <h4>Vulnerabilities</h4>
      <div class="damage-types">
        <StatisticIcon v-for="dt in vulnerabilities" :key="dt" :name="dt" :icon="dt" :value="''" constant condensed />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { titleCase, stringify } from '../util'
import { type CharacterSheetData } from '../statblock/statblock'
import SkillRow from './SkillRow.vue'
import StatisticIcon from './StatisticIcon.vue'
import { joinDice } from '~/assets/dice/diceEval'

const props = defineProps<{
  stats: CharacterSheetData
}>()

const allSaves = computed(() => {
  const customSaves = Object.fromEntries(
    Object.entries(props.stats.customSaves || {}).map(([name, expr]) => [
      name,
      {
        name,
        expr,
        label: `${titleCase(name)} save`,
        level: 'custom',
      },
    ])
  )

  const baseSaves = Object.fromEntries(
    Object.entries({
      str: 'strength',
      dex: 'dexterity',
      con: 'constitution',
      int: 'intelligence',
      wis: 'wisdom',
      cha: 'charisma',
    }).map(([stat, name]) => [
      name,
      {
        name,
        expr: diceExpr(stat),
        label: `${titleCase(name)} save`,
        level: level(stat),
      },
    ])
  )

  return Object.values({ ...baseSaves, ...customSaves })
})

const extractDamageTypes = (damageTypes?: string | string[]) => {
  if (!damageTypes) return []
  if (Array.isArray(damageTypes)) return damageTypes.map((dt) => dt.toLowerCase())
  return damageTypes
    .split(/[,;&] *(and *)?/)
    .filter((dt) => !!dt)
    .map((dt) => dt.toLowerCase())
}

const resistances = computed(() => extractDamageTypes(props.stats.resistances))
const immunities = computed(() => extractDamageTypes(props.stats.immunities))
const vulnerabilities = computed(() => extractDamageTypes(props.stats.vulnerabilities))

const diceExpr = (stat: string) => {
  const prof = (props.stats.saves || []).includes(stat.toUpperCase()) ? '+prof' : ''
  const bonus = props.stats.savesBonus ? stringify(props.stats.savesBonus) : ''
  return joinDice(stat, prof, bonus) ?? '0'
}

const level = (stat: string) => {
  if ((props.stats.saves || []).includes(stat.toUpperCase())) return 'proficiency'
  return ''
}
</script>

<style scoped lang="postcss">
.saves {
  grid-area: saves;
  display: flex;
  flex-direction: column;

  h4 {
    margin-top: 0;
    text-align: center;
    border-bottom: 2px solid var(--primary);

    &:not(:first-child) {
      margin-top: 0.5em;
    }
  }

  .damage-types {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    padding-top: 0.25em;
  }
}
</style>
